import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Shield, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Parental gate shown before any payment or sensitive parental action.
 * Required by Apple/Google policies for kids-directed apps: a simple age gate
 * that a young child cannot pass unassisted. We ask a small arithmetic question
 * (two 2-digit numbers) that only a literate parent can answer quickly.
 */
export default function ParentalGate({ open, onPass, onClose }) {
  const { t } = useTranslation();
  const { a, b } = useMemo(() => ({
    a: 10 + Math.floor(Math.random() * 30),
    b: 10 + Math.floor(Math.random() * 30),
  }), [open]);
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  // Reset stale state whenever the modal is opened
  useEffect(() => {
    if (open) { setValue(""); setError(false); }
  }, [open]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (Number(value) === a + b) {
      setValue("");
      setError(false);
      onPass();
    } else {
      setError(true);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur"
        data-testid="parental-gate"
      >
        <motion.div
          initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }}
          className="glass-strong rounded-3xl p-6 sm:p-8 max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2 text-emerald-300 text-xs font-bold uppercase tracking-[0.3em]">
              <Shield className="w-4 h-4" /> {t("parental_gate.kicker")}
            </div>
            <button onClick={onClose} className="text-stone-400 hover:text-stone-100" data-testid="parental-gate-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <h3 className="font-serif text-2xl font-black text-stone-50">{t("parental_gate.title")}</h3>
          <p className="mt-1 text-sm text-stone-300">{t("parental_gate.desc")}</p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-3">
            <label className="block">
              <span className="block text-xs uppercase tracking-widest font-bold text-stone-300 mb-2">
                {t("parental_gate.question", { a, b })}
              </span>
              <Input
                type="number"
                value={value}
                onChange={(e) => { setValue(e.target.value); setError(false); }}
                className="bg-black/40 border-white/15 text-xl font-mono h-14 text-center"
                autoFocus
                data-testid="parental-gate-input"
                inputMode="numeric"
              />
            </label>
            {error && <p className="text-sm text-rose-300" data-testid="parental-gate-error">{t("parental_gate.wrong")}</p>}
            <Button
              type="submit"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-bold rounded-full py-5"
              data-testid="parental-gate-submit"
            >
              {t("parental_gate.confirm")}
            </Button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
